import type { GridsterEngine } from './gridster-engine';

const INTERVAL_DURATION = 40;

let scrollSensitivity = 10;
let scrollSpeed = 20;
let gridsterElement: HTMLElement | null = null;
let animationV: number | null = null;
let animationH: number | null = null;
let scrollN = false;
let scrollS = false;
let scrollE = false;
let scrollW = false;

export type GridsterScrollItem = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export const scroll = (
  gridster: GridsterEngine,
  item: GridsterScrollItem,
  calculateItemPosition: () => void
): void => {
  const el = gridster.el;
  if (!el) {
    return;
  }

  scrollSensitivity = gridster.options.scrollSensitivity;
  scrollSpeed = gridster.options.scrollSpeed;
  gridsterElement = el;

  const offsetLeft = el.scrollLeft;
  const offsetTop = el.scrollTop;
  const gridRect = el.getBoundingClientRect();
  const itemTop = gridRect.top + gridster.paddingTop + item.top - offsetTop;
  const itemBottom = itemTop + item.height;
  const itemLeft = gridster.paddingLeft + item.left - offsetLeft;
  const itemRight = itemLeft + item.width;

  const canScrollUp = window.scrollY > 0;
  const canScrollDown =
    window.scrollY + window.innerHeight < document.documentElement.scrollHeight;
  const canScrollLeft = offsetLeft > 0;
  const canScrollRight = offsetLeft + el.clientWidth < el.scrollWidth;

  if (itemBottom > window.innerHeight - scrollSensitivity && canScrollDown) {
    cancelN();
    if (!scrollS) {
      startVerticalScroll(1, calculateItemPosition);
    }
  } else if (itemTop < scrollSensitivity && canScrollUp) {
    cancelS();
    if (!scrollN) {
      startVerticalScroll(-1, calculateItemPosition);
    }
  } else {
    cancelVerticalScroll();
  }

  if (itemRight > el.clientWidth - scrollSensitivity && canScrollRight) {
    cancelW();
    if (!scrollE) {
      startHorizontalScroll(1, calculateItemPosition);
    }
  } else if (itemLeft < scrollSensitivity && canScrollLeft) {
    cancelE();
    if (!scrollW) {
      startHorizontalScroll(-1, calculateItemPosition);
    }
  } else {
    cancelHorizontalScroll();
  }
};

export const cancelScroll = (): void => {
  cancelHorizontalScroll();
  cancelVerticalScroll();
  gridsterElement = null;
};

const startVerticalScroll = (sign: number, calculateItemPosition: () => void): void => {
  if (sign > 0) {
    scrollS = true;
  } else {
    scrollN = true;
  }

  let lastUpdate: number | undefined;

  const callback = (timestamp: number) => {
    if (lastUpdate === undefined) {
      lastUpdate = timestamp;
      animationV = window.requestAnimationFrame(callback);
      return;
    }
    if (!gridsterElement) {
      cancelVerticalScroll();
      return;
    }

    const delta = (timestamp - lastUpdate) / INTERVAL_DURATION;
    lastUpdate = timestamp;
    const amount = sign * Math.round(scrollSpeed * delta);

    window.scrollBy(0, amount);
    calculateItemPosition();
    animationV = window.requestAnimationFrame(callback);
  };

  animationV = window.requestAnimationFrame(callback);
};

const startHorizontalScroll = (sign: number, calculateItemPosition: () => void): void => {
  if (sign > 0) {
    scrollE = true;
  } else {
    scrollW = true;
  }

  let lastUpdate: number | undefined;

  const callback = (timestamp: number) => {
    if (lastUpdate === undefined) {
      lastUpdate = timestamp;
      animationH = window.requestAnimationFrame(callback);
      return;
    }
    if (!gridsterElement) {
      cancelHorizontalScroll();
      return;
    }

    const delta = (timestamp - lastUpdate) / INTERVAL_DURATION;
    lastUpdate = timestamp;
    const amount = sign * Math.round(scrollSpeed * delta);

    gridsterElement.scrollLeft += amount;
    calculateItemPosition();
    animationH = window.requestAnimationFrame(callback);
  };

  animationH = window.requestAnimationFrame(callback);
};

const cancelHorizontalScroll = (): void => {
  if (animationH !== null) {
    window.cancelAnimationFrame(animationH);
    animationH = null;
  }
  scrollE = false;
  scrollW = false;
};

const cancelVerticalScroll = (): void => {
  if (animationV !== null) {
    window.cancelAnimationFrame(animationV);
    animationV = null;
  }
  scrollN = false;
  scrollS = false;
};

const cancelE = (): void => {
  if (scrollE) {
    cancelHorizontalScroll();
  }
};

const cancelW = (): void => {
  if (scrollW) {
    cancelHorizontalScroll();
  }
};

const cancelS = (): void => {
  if (scrollS) {
    cancelVerticalScroll();
  }
};

const cancelN = (): void => {
  if (scrollN) {
    cancelVerticalScroll();
  }
};
